import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/loading.css';
import '../../styles/styles.css';
import apiFetch from "../../Api";

const ExamCreationPage = () => {
    const [examName, setExamName] = useState('');
    const [file, setFile] = useState(null);
    const [content, setContent] = useState('');
    const [selectedModel, setSelectedModel] = useState('openAiChatModel');
    const [timePerQuestionMinutes, setTimePerQuestionMinutes] = useState('1');
    const [questionNumber, setQuestionNumber] = useState('1');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isInFullscreen, setIsInFullscreen] = useState(false);

    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const enterFullscreen = () => {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen()
                .catch(err => {
                    console.warn(`Failed to enter fullscreen: ${err.message}`);
                    setMessage({ type: 'error-message', text: 'Nie udało się wejść w tryb pełnoekranowy. Proszę zezwolić przeglądarce na ten tryb.' });
                });
        } else {
            setMessage({ type: 'error-message', text: 'Twoja przeglądarka nie obsługuje trybu pełnoekranowego.' });
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!document.fullscreenElement;
            setIsInFullscreen(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        setIsInFullscreen(!!document.fullscreenElement);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isInFullscreen) {
            setMessage({ type: 'error-message', text: 'Musisz być w trybie pełnoekranowym, aby rozpocząć egzamin.' });
            return;
        }

        setIsLoading(true);
        setMessage({ type: '', text: '' });

        const formData = new FormData();
        formData.append('examName', examName);
        formData.append('content', content);
        formData.append('modelName', selectedModel);
        formData.append('timePerQuestionMinutes', timePerQuestionMinutes);
        formData.append('questionNumber', questionNumber);
        if (file) {
            formData.append('file', file);
        }

        try {
            const response = await apiFetch('/api/exam/create', {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            const data = await response.json();

            if (response.ok) {
                navigate('/exam/current', { state: { exam: data } });
            } else {
                setMessage({ type: 'error-message', text: data.errorMessage || 'Nie udało się stworzyć egzaminu.' });
            }
        } catch (error) {
            console.error('Błąd podczas tworzenia egzaminu:', error);
            setMessage({ type: 'error-message', text: 'Wystąpił błąd sieci lub serwera.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleFileCardClick = () => {
        if (fileInputRef.current && !isLoading) {
            fileInputRef.current.click();
        }
    };

    const timeOptions = [
        { value: '1', label: '1 minuta' },
        { value: '2', label: '2 minuty' },
        { value: '5', label: '5 minut' }
    ];

    const questionNumberOptions = [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '5', label: '5' },
        { value: '10', label: '10' }
    ];

    return (
        <div>
            <div className="container">
                {/* Komunikat globalny (np. błąd serwera) pozostaje na górze */}
                {message.text && (
                    <div className={`message ${message.type}`}>
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="form-container">
                    <div className="form-group">
                        <label htmlFor="examName">Nazwa egzaminu:</label>
                        <input
                            id="examName"
                            type="text"
                            name="examName"
                            placeholder="Wprowadź nazwę egzaminu"
                            required
                            value={examName}
                            onChange={(e) => setExamName(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Nowa sekcja dla wyboru pliku */}
                    <div className="form-group">
                        <label htmlFor="examFile">Wgraj plik PDF</label>
                        <div
                            className={`file-upload-card ${file ? 'selected' : ''} ${isLoading ? 'disabled' : ''}`}
                            onClick={handleFileCardClick}
                            title={file ? file.name : "Kliknij, aby wybrać plik PDF"}
                        >
                            {file ? (
                                <>
                                    <p className="file-name">{file.name}</p>
                                    <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                </>
                            ) : (
                                <p>Wybierz plik PDF (max. 5MB)</p>
                            )}
                        </div>
                        {/* Ukryty input file, aktywowany kliknięciem na div */}
                        <input
                            id="examFile"
                            type="file"
                            name="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            disabled={isLoading}
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="examContent">Wypisz słowa kluczowe</label>
                        <textarea
                            id="examContent"
                            name="content"
                            rows="6"
                            placeholder="Co chcesz przećwiczyć? Wypisz słowa kluczowe, których będzie dotyczył egzamin próbny."
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            disabled={isLoading}
                        ></textarea>
                    </div>

                    <hr />
                    <h3>Wybierz model AI:</h3>
                    <div className="model-selection-container">
                        <div
                            className={`model-card ${selectedModel === 'openAiChatModel' ? 'selected' : ''}`}
                            onClick={() => !isLoading && setSelectedModel('openAiChatModel')}
                        >
                            <h3>OpenAI GPT-4.1 nano</h3>
                            <p>Stworzony przez OpenAI najszybszy i najbardziej efektywny model w stosunku cena-jakość</p>
                        </div>

                        <div
                            className={`model-card ${selectedModel === 'vertexAiGeminiChat' ? 'selected' : ''}`}
                            onClick={() => !isLoading && setSelectedModel('vertexAiGeminiChat')}
                        >
                            <h3>Vertex AI Gemini 2.0 Flash</h3>
                            <p>Ultraszybki model stworzony przez Google do codziennych zadań.</p>
                        </div>

                        <div
                            className={`model-card ${selectedModel === 'anthropicChatModel' ? 'selected' : ''}`}
                            onClick={() => !isLoading && setSelectedModel('anthropicChatModel')}
                        >
                            <h3>Anthropic Claude Opus-4</h3>
                            <p>Najdroższy, zaawansowany model, idealny do złożonych zagadnień.</p>
                        </div>
                    </div>
                    <hr />

                    <h3>Czas na jedno pytanie:</h3>
                    <div className="option-selection-container">
                        {timeOptions.map((option) => (
                            <div
                                key={option.value}
                                className={`option-card ${timePerQuestionMinutes === option.value ? 'selected' : ''}`}
                                onClick={() => !isLoading && setTimePerQuestionMinutes(option.value)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                    <hr />

                    <h3>Liczba pytań:</h3>
                    <div className="option-selection-container">
                        {questionNumberOptions.map((option) => (
                            <div
                                key={option.value}
                                className={`option-card ${questionNumber === option.value ? 'selected' : ''}`}
                                onClick={() => !isLoading && setQuestionNumber(option.value)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                    <hr />

                    {/* PRZENIESIONY WARNING MESSAGE */}
                    {!isInFullscreen && !isLoading && (
                        <div
                            className="warning-message fullscreen-prompt"
                            onClick={enterFullscreen}
                            title="Kliknij, aby przejść do trybu pełnoekranowego"
                            style={{ marginBottom: '20px' }} /* Dodaj margines, aby oddzielić od przycisku */
                        >
                            Aby rozpocząć egzamin, musisz być w trybie pełnoekranowym. Kliknij tutaj, aby przejść do pełnego ekranu.
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '20px' }}>
                        <input
                            type="submit"
                            value="Rozpocznij egzamin"
                            disabled={isLoading || !isInFullscreen}
                            className={!isInFullscreen ? 'disabled-button' : ''}
                        />
                    </div>
                </form>
            </div>
            {isLoading && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>Generowanie egzaminu, proszę czekać...</p>
                </div>
            )}
        </div>
    );
};

export default ExamCreationPage;